import React from 'react'
import _ from 'lodash'
import Loadable from 'react-loadable'

import { actionCreators as quickassActionCreators } from 'reduxBits/quickass'
import { actionCreators as classifiedActionCreators } from 'reduxBits/classified/actions'
import { actionCreators as searchParamsActionCreators } from 'reduxBits/searchParams'
import { actionCreators as accountActionCreators } from 'reduxBits/account'
import { actionCreators as contentfulActionCreators } from 'reduxBits/contentfulPage'

import ErrorComponent from 'components/ErrorComponent'
import Root from './Root'
import SearchParam from './Filter/SearchParam'
import paths from './paths'

const Saved = Loadable({
  loader: () => import('./Saved'),
  loading: () => null,
})

export { Root }

const loadAd = async (ctx, { params }, staticContext) => {
  await ctx.store.dispatch(classifiedActionCreators.fetchAd(params.adId))

  const errorId = _.get(ctx.store.getState(), 'classified.requestedAd.errorId')

  if (errorId === 'no_ads_found') {
    staticContext.responseStatus = 404
  }
}

export const routesArray = [
  {
    path: paths.AdWatchList,
    component: Loadable({
      loader: () => import(/* webpackPrefetch: 80 */ './AdWatchList'),
      loading: () => null,
    }),
  },
  {
    path: paths.CreateAdWatch,
    component: Loadable({
      loader: () => import('./List/FilterSave/CreateAdWatch/CreateAdwatchState'),
      loading: () => null,
    }),
  },
  {
    path: paths.Debug,
    component: Loadable({
      loader: () => import('./Debug'),
      loading: () => null,
    }),
  },
  {
    path: paths.ListRealEstate,
    component: Loadable({
      loader: () => import('./ListRealEstate'),
      loading: () => null,
    }),
    loadData: async (ctx) => {
      await ctx.store.dispatch(searchParamsActionCreators.updateActiveParameters())
    },
  },
  {
    // :region, :categoryGroup, :mainCategory, :subCategory
    // should not be used as a source of truth. The truth is in the query string.
    path: paths.List,
    component: Loadable({
      loader: () => import(/* webpackPrefetch: 100 */ './List'),
      loading: () => null,
    }),
    loadData: async (ctx) => {
      await ctx.store.dispatch(searchParamsActionCreators.updateActiveParameters())
      await ctx.store.dispatch(classifiedActionCreators.fetchAds(ctx.query))
      await ctx.store.dispatch(quickassActionCreators.fetchQuickassCategories(ctx.query))
    },
  },
  {
    path: paths.AdReplyView,
    component: Loadable({
      loader: () => import('external/AdReplyView'),
      loading: () => null,
    }),
    loadData: loadAd,
  },
  {
    path: paths.DirectPurchase,
    component: Loadable({
      loader: () => import('external/BlocketMotor/AdMotor/DirectPurchase/DirectPurchase'),
      loading: () => null,
    }),
    loadData: loadAd,
  },
  {
    path: paths.CarAuction,
    component: Loadable({
      loader: () => import('external/BlocketMotor/AdMotor/CarAuction/CarAuction'),
      loading: () => null,
    }),
  },
  {
    path: paths.FinancePage,
    component: Loadable({
      loader: () => import('external/BlocketMotor/FinancePage'),
      loading: () => null,
    }),
    loadData: loadAd,
  },
  {
    // region should be lowercased and åäö replaced with aao.
    // adTitle must be lowercased and åäö replaced with aao. Any remaining
    // characters matching /\W/ are replaced with a `_`.
    path: paths.Ad,
    component: Loadable({
      loader: () => import(/* webpackPrefetch: 90 */ './Ad'),
      loading: () => null,
    }),
    loadData: loadAd,
  },

  {
    path: paths.Saved,
    component: Saved,
  },

  {
    path: paths.SavedAds,
    component: Saved,
    loadData: async (ctx) => {
      if (ctx.store.getState().authentication.loggedIn) {
        await ctx.store.dispatch(accountActionCreators.fetchSavedAds())
      }
    },
  },
  {
    path: paths.SearchParams,
    component: SearchParam,
    loadData: async (ctx) => {
      await ctx.store.dispatch(searchParamsActionCreators.updateActiveParameters())
      await ctx.store.dispatch(classifiedActionCreators.fetchSearchTotal(ctx.query))
    },
  },
  {
    path: paths.Filter,
    component: Loadable({
      loader: () => import('./Filter'),
      loading: () => null,
    }),
    loadData: async (ctx) => {
      await ctx.store.dispatch(searchParamsActionCreators.updateActiveParameters())
      await ctx.store.dispatch(classifiedActionCreators.fetchSearchTotal(ctx.query))
    },
  },
  {
    path: paths.Store,
    component: Loadable({
      loader: () => import('./Store'),
      loading: () => null,
    }),
    loadData: async (ctx, { params }) => {
      const { getState } = ctx.store
      await ctx.store.dispatch(classifiedActionCreators.fetchStore(params.storeName))
      const storeId = _.get(getState(), 'classified.store.data.id')
      await ctx.store.dispatch(
        classifiedActionCreators.fetchAds({
          st: 'a',
          ...ctx.query,
          storeId,
        }),
      )
      await ctx.store.dispatch(
        searchParamsActionCreators.updateActiveParameters(),
      ) /** What does this function do? */
    },
  },
  {
    path: paths.Categories,
    component: Loadable({
      loader: () => import('./Categories'),
      loading: () => null,
    }),
  },
  {
    path: paths.Stores,
    component: Loadable({
      loader: () => import('./Stores'),
      loading: () => null,
    }),
  },
  {
    path: paths.adNotFound,
    component: () => <ErrorComponent error="ad_not_found" />,
  },
  {
    path: paths.errorPage,
    component: () => <ErrorComponent error="500" />,
  },
  {
    path: paths.Tips,
    component: Loadable({
      loader: () => import(/* webpackPrefetch: 20 */ './ContentfulPage'),
      loading: () => null,
    }),
  },
  {
    path: paths.AppraiseCarValue,
    component: Loadable({
      loader: () => import(/* webpackPrefetch: 20 */ './AppraiseCarValue'),
      loading: () => null,
    }),
  },
  {
    path: paths.SellCarLandingPage,
    component: Loadable({
      loader: () => import(/* webpackPrefetch: 20 */ './SellCarLandingPage'),
      loading: () => null,
    }),
  },
  {
    path: paths.ContentfulBasedPage,
    component: Loadable({
      loader: () => import(/* webpackPrefetch: 10 */ './GraphQLContentfulPage'),
      loading: () => null,
    }),
    loadData: async (ctx) => {
      await ctx.store.dispatch(contentfulActionCreators.fetchContentfulData(ctx.url))
    },
  },
  {
    exact: true,
    path: paths.StartPage,
    component: Loadable({
      loader: () => import('./GraphQLContentfulPage'),
      loading: () => null,
    }),
    loadData: async (ctx) => {
      await ctx.store.dispatch(quickassActionCreators.fetchQuickassCategories(ctx.query))
    },
  },
  {
    path: '*',
    component: () => <ErrorComponent error="404" />,
  },
]
